/** A place's coordinates: two small inputs and the map one click away in a popover. Typing
 *  coalesces per focus session; a pick on the map is a step of its own.
 *
 *  - `createCoordinatesWidget` — two paths edited as one field (the tour's meeting point,
 *    plan §10.1 B-lift): the latitude is the widget's own field, the longitude rides with
 *    it; every edit sets both paths in one undo step.
 *  - `createCoordinatePairWidget` — ONE key whose value is `{latitude, longitude}` (a
 *    catalog record's place, the activity's `coordinates`): both or neither — clearing both
 *    is null — each in range; it reads "41.7676, -73.9358".
 *
 *  Zero re-layout: the inputs keep the height of the view-mode value; no map appears on
 *  Edit. */
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { Box, IconButton, Popover, TextField, Tooltip } from '@mui/material';
import { useEffect, useRef, useState, type Ref } from 'react';

import { LatLngPicker } from '@/components/LatLngPicker/LatLngPicker';
import { parseRecordKey } from '@/features/records/draft/changeSet';
import { useDraft, useDraftField } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { RecordWidget, RecordWidgetProps } from '@/features/records/editor/types';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';

export interface CoordinatesPaths {
  /** The latitude's path — the field the widget is registered for. */
  lat: string;
  /** The longitude's path on the same record. A ledger row for it focuses the longitude
   *  input (it has no slot of its own). */
  lng: string;
  /** What the coordinates place, for the map button's name and the map's undo label
   *  ("meeting point" → "Pick the meeting point on the map"). */
  place: string;
}

function parseCoord(raw: string): number | null | 'invalid' {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : 'invalid';
}

let coordSession = 0;

function CoordInput({
  id,
  label,
  value,
  onValue,
  onInvalid,
  onFocus,
  onBlur,
  error,
  disabled,
  describedBy,
  inputRef,
  changed,
}: {
  id?: string;
  label: string;
  value: unknown;
  onValue: (v: number | null) => void;
  onInvalid: (message: string | null) => void;
  onFocus: () => void;
  onBlur: () => void;
  error: boolean;
  disabled: boolean;
  describedBy: string;
  inputRef?: Ref<HTMLInputElement>;
  /** This half differs from the stored value (the slot tints only for the latitude). */
  changed?: boolean;
}) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (typeof value === 'number' ? String(value) : '');
  return (
    <TextField
      size="small"
      value={shown}
      error={error}
      disabled={disabled}
      placeholder={label}
      onFocus={onFocus}
      onBlur={() => {
        if (text !== null && parseCoord(text) !== 'invalid') setText(null);
        onBlur();
      }}
      onChange={(e) => {
        setText(e.target.value);
        const v = parseCoord(e.target.value);
        if (v === 'invalid') onInvalid(`${label}: enter a number`);
        else {
          onInvalid(null);
          onValue(v);
        }
      }}
      inputProps={{ id, 'aria-label': label, 'aria-describedby': describedBy, inputMode: 'decimal', autoComplete: 'off' }}
      inputRef={inputRef}
      sx={[{ flex: '1 1 0', minWidth: 0 }, Boolean(changed) && { '& .MuiOutlinedInput-root': { bgcolor: CHANGED_TINT } }]}
    />
  );
}

/** The coordinates widget for one pair of paths — create once per record kind. */
export function createCoordinatesWidget({ lat: latPath, lng: lngPath, place }: CoordinatesPaths): RecordWidget {
  function CoordinatesWidget(p: RecordWidgetProps) {
    const { dispatch, baselineOf } = useDraft();
    const { registerFocus } = useRecordEditor();
    const { entity, id } = parseRecordKey(p.rec);
    const lng = useDraftField(entity, id, lngPath);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const session = useRef<string | null>(null);
    const lngInput = useRef<HTMLInputElement>(null);
    const lat = typeof p.value === 'number' ? p.value : null;
    const lngValue = typeof lng.value === 'number' ? lng.value : null;

    // A ledger row for the longitude lands here (it has no slot of its own).
    useEffect(() => registerFocus(p.rec, lngPath, () => lngInput.current?.focus()), [registerFocus, p.rec]);

    const set = (next: { lat: number | null; lng: number | null }, label?: string) =>
      dispatch({
        type: 'SET',
        changes: [
          { rec: p.rec, path: latPath, from: baselineOf(p.rec, latPath), to: next.lat },
          { rec: p.rec, path: lngPath, from: baselineOf(p.rec, lngPath), to: next.lng },
        ],
        ...(label ? { label } : {}),
        ...(session.current ? { coalesce: session.current } : {}),
      });
    const begin = () => {
      coordSession += 1;
      session.current = `${p.rec}|coordinates#${coordSession}`;
    };
    const end = () => {
      session.current = null;
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
        <CoordInput
          id={p.id}
          label="Latitude"
          value={lat}
          onValue={(v) => set({ lat: v, lng: lngValue })}
          onInvalid={p.onInvalid}
          onFocus={begin}
          onBlur={end}
          error={p.error}
          disabled={p.disabled}
          describedBy={p.describedBy}
        />
        <CoordInput
          label="Longitude"
          value={lngValue}
          onValue={(v) => set({ lat, lng: v })}
          onInvalid={p.onInvalid}
          onFocus={begin}
          onBlur={end}
          error={p.error}
          disabled={p.disabled}
          describedBy={p.describedBy}
          inputRef={lngInput}
          changed={lng.changed}
        />
        <Tooltip title="Pick on the map">
          <IconButton size="small" aria-label={`Pick the ${place} on the map`} onClick={(e) => setAnchor(e.currentTarget)} disabled={p.disabled}>
            <MapOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { p: 1.5, width: 420, maxWidth: '90vw' } } }}
        >
          <LatLngPicker value={{ lat, lng: lngValue }} onChange={(v) => set(v, `Move the ${place} on the map`)} />
        </Popover>
      </Box>
    );
  }
  return CoordinatesWidget;
}

// ---------------------------------------------------------------------------
// ONE key: {latitude, longitude}
// ---------------------------------------------------------------------------

export interface CoordinatePair {
  latitude: number | null;
  longitude: number | null;
}

/** A stored or drafted pair, whatever its shape (null: neither). */
export function asCoordinatePair(v: unknown): CoordinatePair {
  const o = v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? x : null);
  return { latitude: num(o.latitude), longitude: num(o.longitude) };
}

/** The wire value of a pair: null when neither is set. */
export function coordinatePairValue(p: CoordinatePair): CoordinatePair | null {
  return p.latitude === null && p.longitude === null ? null : p;
}

/** Why a pair cannot be saved, or null: both or neither, each in range. */
export function coordinatePairError(v: unknown): string | null {
  const { latitude, longitude } = asCoordinatePair(v);
  if ((latitude === null) !== (longitude === null)) return 'Enter both the latitude and the longitude, or neither';
  if (latitude !== null && (latitude < -90 || latitude > 90)) return 'The latitude is between -90 and 90';
  if (longitude !== null && (longitude < -180 || longitude > 180)) return 'The longitude is between -180 and 180';
  return null;
}

/** "41.7676, -73.9358"; a missing half reads "—". */
export function formatCoordinatePair(v: unknown): string {
  const { latitude, longitude } = asCoordinatePair(v);
  return `${latitude ?? '—'}, ${longitude ?? '—'}`;
}

/** The coordinates widget for one key holding `{latitude, longitude}` — create once per
 *  record kind. `place` names what they place ("activity" → "Pick the activity on the
 *  map"). */
export function createCoordinatePairWidget({ place }: { place: string }): RecordWidget {
  function CoordinatePairInput(p: RecordWidgetProps) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const pair = asCoordinatePair(p.value);
    const set = (next: CoordinatePair, label?: string) => p.onChange(coordinatePairValue(next), label ? { label } : undefined);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
        <CoordInput
          id={p.id}
          label="Latitude"
          value={pair.latitude}
          onValue={(v) => set({ latitude: v, longitude: pair.longitude })}
          onInvalid={p.onInvalid}
          onFocus={p.onFocus}
          onBlur={p.onBlur}
          error={p.error}
          disabled={p.disabled}
          describedBy={p.describedBy}
        />
        <CoordInput
          label="Longitude"
          value={pair.longitude}
          onValue={(v) => set({ latitude: pair.latitude, longitude: v })}
          onInvalid={p.onInvalid}
          onFocus={p.onFocus}
          onBlur={p.onBlur}
          error={p.error}
          disabled={p.disabled}
          describedBy={p.describedBy}
        />
        <Tooltip title="Pick on the map">
          <IconButton size="small" aria-label={`Pick the ${place} on the map`} onClick={(e) => setAnchor(e.currentTarget)} disabled={p.disabled}>
            <MapOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { p: 1.5, width: 420, maxWidth: '90vw' } } }}
        >
          <LatLngPicker
            value={{ lat: pair.latitude, lng: pair.longitude }}
            onChange={(v) => set({ latitude: v.lat, longitude: v.lng }, `Move the ${place} on the map`)}
          />
        </Popover>
      </Box>
    );
  }
  return Object.assign(CoordinatePairInput, {
    formatChange: formatCoordinatePair,
    checkValue: (to: unknown) => coordinatePairError(to),
  });
}
